import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { InboundEmailFile } from '../types';
import {
  createInboundEmail,
  listInboundEmailFiles,
  deleteInboundEmail,
  type ApiClient,
  type InboundEmailCreateOptions,
  type InboundEmailFileResponse,
} from '../utils/api';
import { classifyError } from '../utils/errors';
import { friendlyCasType } from '../utils/extractSummary';
import {
  Button,
  PasswordInput,
  Spinner,
  StatusBanner,
  ScreenHeader,
  LinkButton,
} from './ui';
import {
  IconForwardEmail,
  IconCopy,
  IconCheck,
  IconShield,
  IconAlert,
} from './icons';

interface InboundEmailModeProps {
  client: ApiClient;
  /**
   * Optional fallback handler — when provided, the error view surfaces a
   * primary CTA that jumps back to the Gmail import tab. Used when the
   * caller wants to let users recover gracefully if inbound email is
   * unavailable on their API tier.
   */
  onSwitchToGmail?: () => void;
  /**
   * Optional escape from the WAITING screen — surfaced as "Already have
   * the file? Upload it directly". Bubbles up to the parent which
   * navigates the widget to FILE_UPLOAD. Tearing down the inbound
   * session is handled by this component's unmount cleanup.
   */
  onJumpToUpload?: () => void;
  config?: {
    callbackUrl?: string;
    existingId?: string;
    email?: string;
    allowedSources?: ('cdsl' | 'nsdl' | 'cams' | 'kfintech')[];
    reference?: string;
    metadata?: Record<string, string>;
    pollIntervalMs?: number;
    sessionTimeoutMs?: number;
  };
  prefillPan?: string;
  /**
   * Pre-fill the password field on the post-file screen. Used when the
   * user reached the inbound flow via the generator handoff — the
   * generated CAS is encrypted with a known password (the SDK default
   * or `config.generator.password`), and the embedder shouldn't make
   * the user type it. Falls back to `prefillPan` when not provided
   * (sensible default for downloaded-from-portal CAS PDFs).
   */
  prefillPassword?: string;
  onFileSelect: (file: InboundEmailFile, password: string) => void;
  onBack: () => void;
  onEvent?: (event: string, metadata?: any) => void;
}

type ViewState = 'PROVISIONING' | 'WAITING' | 'PASSWORD' | 'TIMED_OUT' | 'ERROR';

const DEFAULT_POLL_INTERVAL_MS = 3000;
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min

export const InboundEmailMode: React.FC<InboundEmailModeProps> = ({
  client,
  onSwitchToGmail,
  onJumpToUpload,
  config = {},
  prefillPan,
  prefillPassword,
  onFileSelect,
  onBack,
  onEvent,
}) => {
  const [view, setView] = useState<ViewState>('PROVISIONING');
  const [error, setError] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  // HTTP status from the API rejection (when available). Lets the
  // ERROR view branch on status-code semantics ("400/422 = config
  // wasn't accepted" → suggest Gmail / upload fallback) instead of
  // string-matching the API's English message text.
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [inboundEmailId, setInboundEmailId] = useState<string | null>(null);
  const [inboundEmail, setInboundEmail] = useState<string | null>(null);
  const [receivedFile, setReceivedFile] = useState<InboundEmailFile | null>(null);
  // Prefer the caller-supplied generator password (exact match for the
  // CAS that's about to arrive); fall back to PAN (the convention for
  // downloaded-from-portal CAS); fall back to empty.
  const [password, setPassword] = useState(prefillPassword || prefillPan || '');
  const [copied, setCopied] = useState(false);

  // Refs for interval/timeout cleanup and cursor tracking across polls
  const cursorRef = useRef<string>('');
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stoppedRef = useRef(false);
  // Track whether this session owns the inbound email (and should delete it on exit)
  const isOwnedRef = useRef(false);
  // Mirror of inboundEmailId so the unmount cleanup (which only closes over
  // initial render state) can see the latest value.
  const inboundEmailIdRef = useRef<string | null>(null);

  const pollInterval = config.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const sessionTimeout = config.sessionTimeoutMs ?? DEFAULT_SESSION_TIMEOUT_MS;

  const emitEvent = useCallback((event: string, metadata?: any) => {
    onEvent?.(event, { timestamp: Date.now(), ...metadata });
  }, [onEvent]);

  // Stop polling and clear all timers
  const stopSession = useCallback(() => {
    stoppedRef.current = true;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
  }, []);

  // Single poll tick: fetch new files since cursor, schedule next tick
  const pollOnce = useCallback(async (ieId: string) => {
    if (stoppedRef.current) return;
    if (document.visibilityState === 'hidden') {
      // Pause while tab is hidden — visibility handler will resume
      pollTimerRef.current = setTimeout(() => pollOnce(ieId), pollInterval);
      return;
    }
    emitEvent('INBOUND_EMAIL_POLLING', { inbound_email_id: ieId, cursor: cursorRef.current });
    try {
      const res = await listInboundEmailFiles(client, ieId, cursorRef.current || undefined);
      if (res.cursor) cursorRef.current = res.cursor;
      if (res.files && res.files.length > 0) {
        // Take the first new file — auto-parse is single-shot per session
        const first = res.files[0] as InboundEmailFileResponse;
        const file: InboundEmailFile = {
          message_id: first.message_id,
          filename: first.filename,
          original_filename: first.original_filename,
          message_date: first.message_date,
          cas_type: first.cas_type,
          sender_email: first.sender_email,
          size: first.size,
          url: first.url,
          expires_in: first.expires_in,
          received_at: first.received_at,
        };
        stopSession();
        setReceivedFile(file);
        emitEvent('INBOUND_EMAIL_FILE_RECEIVED', {
          cas_type: file.cas_type,
          filename: file.filename,
          size: file.size,
        });
        // Always show the password screen — the user must enter the
        // password for the received CAS file.
        setView('PASSWORD');
        return;
      }
    } catch (err: any) {
      emitEvent('INBOUND_EMAIL_ERROR', {
        error: err?.message || 'Polling failed',
      });
      if (!stoppedRef.current) {
        pollTimerRef.current = setTimeout(() => pollOnce(ieId), pollInterval);
      }
      return;
    }

    if (!stoppedRef.current) {
      pollTimerRef.current = setTimeout(() => pollOnce(ieId), pollInterval);
    }
  }, [client, pollInterval, emitEvent, onFileSelect, stopSession]);

  // Resume polling when the tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && inboundEmailId && !stoppedRef.current && !pollTimerRef.current) {
        pollOnce(inboundEmailId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [inboundEmailId, pollOnce]);

  // Provision the inbound email on mount and start polling
  useEffect(() => {
    let cancelled = false;

    const provision = async () => {
      // Scenario C: developer injected an existing inbound email
      if (config.existingId) {
        if (cancelled) return;
        isOwnedRef.current = false;
        inboundEmailIdRef.current = config.existingId;
        setInboundEmailId(config.existingId);
        setInboundEmail(config.email || null);
        setView('WAITING');
        emitEvent('INBOUND_EMAIL_CREATED', {
          inbound_email_id: config.existingId,
          email: config.email,
          owned: false,
        });
        startSession(config.existingId);
        return;
      }

      // Scenario A/B: create one
      try {
        const opts: InboundEmailCreateOptions = {};
        if (config.callbackUrl) opts.callbackUrl = config.callbackUrl;
        if (config.allowedSources?.length) opts.allowedSources = config.allowedSources;
        if (config.reference) opts.reference = config.reference;
        if (config.metadata) opts.metadata = config.metadata;

        const res = await createInboundEmail(client, opts);
        if (cancelled) return;
        isOwnedRef.current = true;
        inboundEmailIdRef.current = res.inbound_email_id;
        setInboundEmailId(res.inbound_email_id);
        setInboundEmail(res.email);
        setView('WAITING');
        emitEvent('INBOUND_EMAIL_CREATED', {
          inbound_email_id: res.inbound_email_id,
          email: res.email,
          owned: true,
        });
        startSession(res.inbound_email_id);
      } catch (err: any) {
        if (cancelled) return;
        const classified = classifyError(err, 'INBOUND_EMAIL_ERROR');
        const status: number | null =
          err?.response?.status ?? err?.status ?? null;
        setErrorTitle(classified.title);
        setError(classified.message);
        setErrorStatus(status);
        setView('ERROR');
        emitEvent('INBOUND_EMAIL_ERROR', {
          error: classified.message,
          code: classified.code,
          status,
          phase: 'create',
        });
      }
    };

    const startSession = (ieId: string) => {
      stoppedRef.current = false;
      // Session timeout
      sessionTimerRef.current = setTimeout(() => {
        stopSession();
        emitEvent('INBOUND_EMAIL_TIMEOUT', { elapsed_ms: sessionTimeout });
        setView('TIMED_OUT');
      }, sessionTimeout);
      // Kick off polling immediately
      pollOnce(ieId);
    };

    provision();

    return () => {
      cancelled = true;
      stopSession();
      // Cleanup only if this session created the inbound email.
      // Read from ref because state captured here is stale (empty-deps effect).
      const idToDelete = inboundEmailIdRef.current;
      if (isOwnedRef.current && idToDelete) {
        deleteInboundEmail(client, idToDelete).catch(() => {
          // Best-effort cleanup; TTL will eventually reclaim it
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async () => {
    if (!inboundEmail) return;
    try {
      await navigator.clipboard.writeText(inboundEmail);
      setCopied(true);
      emitEvent('INBOUND_EMAIL_COPIED', { email: inboundEmail });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text for manual copy — most modern browsers block this
      // in non-secure contexts, but clipboard API usually works on https.
    }
  };

  const handlePasswordSubmit = () => {
    if (!receivedFile) return;
    onFileSelect(receivedFile, password);
  };

  const handleCancel = () => {
    stopSession();
    onBack();
  };

  // ---------- Render ----------

  if (view === 'PROVISIONING') {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }} aria-live="polite">
        <Spinner size={44} />
        <h3
          style={{
            margin: '14px 0 6px',
            fontSize: 17,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
          }}
        >
          Creating your one-time forwarding address…
        </h3>
        <p
          style={{
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          This takes a second. We'll show you what to do next.
        </p>
      </div>
    );
  }

  if (view === 'ERROR') {
    // Detect "this account / API tier doesn't support SDK-mode inbound
    // email" via HTTP-status semantics rather than substring-matching
    // the API's English error text:
    //   - 400 / 422 → config-validation rejection (e.g. callback_url
    //     required by backend policy)
    //   - 403 → feature gated for this tier
    // All three indicate the embedder needs to switch flows
    // (Gmail / direct upload) rather than retry. We override the
    // generic "Session expired" heading that classifyError would
    // otherwise emit for 403 since on /v4/inbound-email specifically,
    // 403 is much more often "tier gating" than "auth failed".
    const isConfigRejected =
      errorStatus === 400 || errorStatus === 403 || errorStatus === 422;
    const heading = isConfigRejected
      ? 'Email forwarding isn\'t available here'
      : errorTitle || 'Couldn\'t start email forwarding';
    const subheading = isConfigRejected
      ? onSwitchToGmail
        ? 'You can connect your inbox to auto-find CAS statements, or upload your file directly.'
        : 'Try uploading your CAS PDF directly — it works with every provider.'
      : error || 'Please try again in a moment.';
    const showFallbackCta = isConfigRejected;

    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <span
          style={{
            display: 'inline-flex',
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: 'var(--pc-color-error-bg)',
            color: 'var(--pc-color-error)',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12,
          }}
        >
          <IconAlert size={24} />
        </span>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
            margin: '0 0 8px',
          }}
        >
          {heading}
        </h3>
        <p
          style={{
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            margin: '0 0 16px',
            lineHeight: 1.5,
          }}
        >
          {subheading}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {showFallbackCta && onSwitchToGmail && (
            <Button variant="primary" onClick={onSwitchToGmail}>
              Connect inbox instead
            </Button>
          )}
          <Button
            variant={showFallbackCta && onSwitchToGmail ? 'secondary' : 'primary'}
            onClick={onBack}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'TIMED_OUT') {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
            margin: '0 0 10px',
          }}
        >
          We haven't seen your email yet
        </h3>
        <p
          style={{
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            margin: '0 0 14px',
            lineHeight: 1.5,
            textAlign: 'left',
          }}
        >
          A few things to check:
        </p>
        <ol
          style={{
            margin: '0 0 18px',
            paddingLeft: 20,
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            lineHeight: 1.7,
            textAlign: 'left',
          }}
        >
          <li>Did you forward to the exact address we showed you?</li>
          <li>Some mobile email apps strip attachments — try forwarding from desktop.</li>
          <li>Check that your statement email actually has a PDF attached.</li>
        </ol>
        <Button variant="primary" fullWidth onClick={onBack}>
          Try a different method
        </Button>
      </div>
    );
  }

  if (view === 'PASSWORD' && receivedFile) {
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <span
            style={{
              display: 'inline-flex',
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'var(--pc-color-success-bg)',
              color: 'var(--pc-color-success)',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            <IconCheck size={26} />
          </span>
          <h3
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--pc-color-text)',
              margin: '0 0 4px',
            }}
          >
            Statement received
          </h3>
          <p
            style={{
              fontSize: 13,
              color: 'var(--pc-color-text-muted)',
              margin: 0,
            }}
          >
            {friendlyCasType(receivedFile.cas_type) || receivedFile.cas_type.toUpperCase()}{' '}
            · {formatBytes(receivedFile.size)}
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--pc-color-text)',
              marginBottom: 6,
            }}
          >
            Statement password
          </label>
          <PasswordInput
            data-pc-autofocus
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password) handlePasswordSubmit();
            }}
            placeholder="Enter your PDF password"
          />
          <p
            style={{
              fontSize: 11,
              color: 'var(--pc-color-text-faded)',
              margin: '6px 0 0',
            }}
          >
            Usually your PAN in uppercase.
          </p>
        </div>

        <Button variant="primary" fullWidth onClick={handlePasswordSubmit} disabled={!password}>
          Import Statement
        </Button>
      </div>
    );
  }

  // WAITING view — the main display
  return (
    <div>
      <ScreenHeader
        icon={<IconForwardEmail size={22} />}
        title="Forward your statement email"
        description="Got your CAS email from CAMS, KFintech, CDSL or NSDL with the statement attached? Forward it to the address below — we'll grab the PDF automatically."
      />

      {/* Email address display */}
      <div
        style={{
          backgroundColor: 'var(--pc-color-surface-subtle)',
          border: '1.5px solid var(--pc-color-border)',
          borderRadius: 'var(--pc-radius-md)',
          padding: '14px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span
          style={{
            flex: 1,
            fontSize: 13,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
            color: 'var(--pc-color-text)',
            wordBreak: 'break-all',
          }}
        >
          {inboundEmail || '—'}
        </span>
        <Button
          variant={copied ? 'secondary' : 'secondary'}
          onClick={handleCopy}
          aria-label="Copy email address"
          iconLeft={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {/* Live status */}
      <div style={{ marginBottom: 16 }}>
        <StatusBanner tone="warning">
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'var(--pc-color-warning)',
              marginRight: 8,
              animation: 'pc-pulse 1.5s ease-in-out infinite',
              verticalAlign: 'middle',
            }}
            aria-hidden="true"
          />
          Waiting for your email to arrive…
        </StatusBanner>
      </div>

      {/* How-to steps */}
      <div style={{ marginBottom: 20 }}>
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
            margin: '0 0 8px',
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
        >
          How to forward
        </p>
        <ol
          style={{
            margin: 0,
            paddingLeft: 20,
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            lineHeight: 1.7,
          }}
        >
          <li>Open your CAS email from CAMS, CDSL or NSDL</li>
          <li>Tap <strong>Forward</strong> (not Reply)</li>
          <li>Paste the address above as the recipient and send</li>
        </ol>
      </div>

      {/* Trust footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          fontSize: 11,
          color: 'var(--pc-color-text-faded)',
        }}
      >
        <IconShield size={14} />
        We only accept emails from official CAS authorities
      </div>

      {/* Escape hatch + cancel. Some users open this screen, then realise
          they actually have the PDF on disk — let them jump to upload
          without first hitting Cancel and re-navigating from the home
          screen. The session cleanup runs on unmount, so the inbound
          address is freed automatically. */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'center',
          marginTop: 14,
          flexWrap: 'wrap',
        }}
      >
        {onJumpToUpload && (
          <LinkButton
            onClick={() => {
              stopSession();
              onJumpToUpload();
            }}
          >
            I already have the file — upload it
          </LinkButton>
        )}
        <LinkButton onClick={handleCancel}>Cancel</LinkButton>
      </div>
    </div>
  );
};

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
}
